import React, { useMemo } from 'react';
import { BRAND, STATUS, FONT_MONO, statusToSemantic } from 'Utils/styleTokens';

const GRID = { display: 'grid', gridTemplateColumns: '44px 1fr 240px 130px 150px 120px', alignItems: 'center' };

const fmtDuration = (ms) => {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const min = s / 60;
  if (min < 60) return `~${Math.round(min)} min`;
  return `~${(min / 60).toFixed(1)} hr`;
};

// Pair each definition step with the latest run's step of the same job identity
// (source + inventory + target); first unclaimed match wins so duplicates pair off
// positionally. Mirrors the platform API's duration pairing.
const pairWithLatestRun = (steps, latestRun) => {
  const unclaimed = latestRun && Array.isArray(latestRun.steps) ? [...latestRun.steps] : [];
  return (steps || []).map((step) => {
    const idx = unclaimed.findIndex(
      (rs) =>
        rs.dataSourceId === step.dataSourceId &&
        rs.inventoryId === step.inventoryId &&
        (rs.targetDataSourceId || null) === (step.targetDataSourceId || null)
    );
    const runStep = idx >= 0 ? unclaimed.splice(idx, 1)[0] : null;
    return { step, runStep };
  });
};

// Group paired rows into display blocks mirroring the backend's fan-out coalescing rule:
// consecutive steps sharing (source, inventory), all with a cross-source target, are ONE
// fan-out block (rendered as a single row "source → t1 · t2 · t3"); everything else is a
// single-step block. Keeping this predicate aligned with MaskingJobGroupRunService's
// fanoutBatchStartingAt means the table's rows match what actually runs as one job.
const blocksOf = (pairedRows) => {
  const blocks = [];
  let i = 0;
  while (i < pairedRows.length) {
    const first = pairedRows[i];
    const block = [first];
    if (first.step.targetDataSourceId != null) {
      let j = i + 1;
      while (
        j < pairedRows.length &&
        pairedRows[j].step.targetDataSourceId != null &&
        pairedRows[j].step.dataSourceId === first.step.dataSourceId &&
        pairedRows[j].step.inventoryId === first.step.inventoryId
      ) {
        block.push(pairedRows[j]);
        j += 1;
      }
    }
    blocks.push(block);
    i += block.length;
  }
  return blocks;
};

const stepStatusLabel = (runStep, runActive) => {
  if (!runStep) return { label: 'Idle', semantic: 'neutral' };
  if (runStep.blockedSince && runStep.status === 'PENDING') {
    return { label: 'Waiting for data source', semantic: 'warning' };
  }
  if (!runActive && runStep.status === 'PENDING') return { label: 'Idle', semantic: 'neutral' };
  const labels = {
    PENDING: 'Queued',
    RUNNING: 'Running',
    SUCCEEDED: 'OK',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
    SKIPPED: 'Skipped',
  };
  return { label: labels[runStep.status] || runStep.status, semantic: statusToSemantic(runStep.status) };
};

function StepsTable({ group, onReplaceSteps, onAddStep, onEditStep, onEdit, onDelete }) {
  const runActive = !!group.runActive;
  const rows = useMemo(
    () => pairWithLatestRun(group.steps, group.latestRun),
    [group.steps, group.latestRun]
  );
  const blocks = useMemo(() => blocksOf(rows), [rows]);

  // Reorder/remove operate on BLOCKS (a fan-out moves and deletes as a unit); a fan-out
  // target chip's ✕ removes just that one step. All paths send the full new step list.
  const replace = (blockList) => onReplaceSteps(blockList.flat().map(({ step }) => step));
  const moveBlock = (blockIdx, dir) => {
    const j = blockIdx + dir;
    if (j < 0 || j >= blocks.length) return;
    const order = blocks.slice();
    [order[blockIdx], order[j]] = [order[j], order[blockIdx]];
    replace(order);
  };
  const removeBlock = (blockIdx) => replace(blocks.filter((_, i) => i !== blockIdx));
  const removeStep = (target) =>
    onReplaceSteps((group.steps || []).filter((s) => s !== target));

  const manageButton = (title, label, onClick, danger) => (
    <button
      type='button'
      title={title}
      disabled={runActive}
      onClick={onClick}
      style={{
        border: '1px solid var(--ink-150)',
        background: '#fff',
        cursor: runActive ? 'not-allowed' : 'pointer',
        fontSize: 11,
        color: danger ? STATUS.danger.text : 'var(--ink-500)',
        width: 26,
        height: 26,
        borderRadius: 6,
        opacity: runActive ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ borderTop: '1px solid var(--ink-150)', background: 'var(--ink-50)', padding: '14px 20px 16px' }}>
      <div
        className='sg-small'
        style={{
          ...GRID,
          padding: '0 0 8px',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--ink-400)',
        }}
      >
        <div>Order</div>
        <div>Masking job — data source</div>
        <div>Inventory</div>
        <div>Duration (last)</div>
        <div>Status</div>
        <div className='text-right'>Manage</div>
      </div>
      {rows.length === 0 && (
        <div className='sg-small py-2' style={{ color: 'var(--ink-400)' }}>
          No steps yet — add a masking job below.
        </div>
      )}
      {blocks.map((block, blockIdx) => {
        const { step, runStep } = block[0];
        const isFanout = block.length > 1;
        const status = stepStatusLabel(runStep, runActive);
        const pill = STATUS[status.semantic] || STATUS.neutral;
        return (
          <div
            key={step.id != null ? step.id : blockIdx}
            data-testid={`step-block-${group.id}-${blockIdx}`}
            style={{
              ...GRID,
              background: '#fff',
              border: '1px solid var(--ink-150)',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 6,
            }}
          >
            <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 600, color: 'var(--ink-400)' }}>
              {String(blockIdx + 1).padStart(2, '0')}
            </div>
            <div
              className='d-flex align-items-center flex-wrap'
              style={{ fontFamily: FONT_MONO, fontSize: 12.5, color: BRAND.blue, gap: 5, minWidth: 0 }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                {step.dataSourceName || (step.dataSourceId != null ? `#${step.dataSourceId}` : 'Deleted')}
              </span>
              {step.targetDataSourceId != null && <span style={{ color: 'var(--ink-400)' }}>→</span>}
              {!isFanout && step.targetDataSourceId != null && (
                <span style={{ color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>
                  {step.targetDataSourceName || `#${step.targetDataSourceId}`}
                </span>
              )}
              {isFanout &&
                block.map(({ step: s }) => (
                  <span
                    key={s.id != null ? s.id : s.targetDataSourceId}
                    className='d-inline-flex align-items-center'
                    style={{
                      gap: 4,
                      fontSize: 11,
                      color: 'var(--ink-700)',
                      background: 'var(--ink-50)',
                      border: '1px solid var(--ink-150)',
                      borderRadius: 6,
                      padding: '1px 6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.targetDataSourceName || `#${s.targetDataSourceId}`}
                    <button
                      type='button'
                      title='Remove this destination'
                      disabled={runActive}
                      onClick={() => removeStep(s)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: runActive ? 'not-allowed' : 'pointer',
                        color: STATUS.danger.text,
                        fontSize: 10,
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              {isFanout && (
                <span className='sg-small' style={{ color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>
                  fan-out · one pass
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
              {step.inventoryName || (step.inventoryId != null ? `#${step.inventoryId}` : 'Deleted')}
            </div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--ink-500)' }}>
              {fmtDuration(step.lastDurationMs != null ? step.lastDurationMs : runStep && runStep.durationMs)}
            </div>
            <div
              className='d-flex align-items-center'
              style={{ gap: 6, fontSize: 11, fontWeight: 600, color: pill.text }}
              title={runStep && runStep.errorMessage ? runStep.errorMessage : undefined}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: pill.dot, flex: 'none' }} />
              {status.label}
            </div>
            <div className='d-flex justify-content-end' style={{ gap: 4 }}>
              {manageButton('Move up', '↑', () => moveBlock(blockIdx, -1))}
              {manageButton('Move down', '↓', () => moveBlock(blockIdx, 1))}
              {manageButton(
                isFanout ? 'Edit fan-out' : 'Edit step',
                '✎',
                () => onEditStep(block.map(({ step: s }) => s))
              )}
              {manageButton(isFanout ? 'Remove fan-out from group' : 'Remove from group', '✕', () => removeBlock(blockIdx), true)}
            </div>
            {/* A failed step's error is shown in full, not hidden behind a hover — spans the
                whole row (gridColumn 1/-1). A fan-out cohort shares one message; distinct
                messages (shouldn't happen, but) each get their own line. */}
            {[...new Set(block
              .filter(({ runStep: rs }) => rs && rs.errorMessage
                && (rs.status === 'FAILED' || rs.status === 'CANCELLED'))
              .map(({ runStep: rs }) => rs.errorMessage))]
              .map((message, errIdx) => (
                <div
                  key={`err-${errIdx}`}
                  className='sg-small'
                  style={{
                    gridColumn: '1 / -1',
                    color: STATUS.danger.text,
                    marginTop: 6,
                    paddingTop: 6,
                    borderTop: '1px dashed var(--ink-150)',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {message}
                </div>
              ))}
          </div>
        );
      })}
      <div className='d-flex align-items-center' style={{ gap: 10, marginTop: 4 }}>
        <button
          type='button'
          disabled={runActive}
          onClick={onAddStep}
          data-testid={`job-group-add-step-${group.id}`}
          style={{
            border: '1px dashed var(--ink-300)',
            background: 'transparent',
            cursor: runActive ? 'not-allowed' : 'pointer',
            fontSize: 11.5,
            fontWeight: 600,
            color: 'var(--ink-500)',
            padding: '7px 14px',
            borderRadius: 7,
            opacity: runActive ? 0.5 : 1,
          }}
        >
          + Add masking job
        </button>
        <span className='sg-small' style={{ color: 'var(--ink-400)' }}>
          Steps run sequentially — a failed step halts the group
        </span>
        <button
          type='button'
          className='ml-auto'
          onClick={onEdit}
          style={{
            border: '1px solid var(--ink-200)',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ink-700)',
            padding: '6px 12px',
            borderRadius: 7,
          }}
        >
          Edit details
        </button>
        <button
          type='button'
          disabled={runActive}
          onClick={onDelete}
          style={{
            border: '1px solid var(--ink-200)',
            background: '#fff',
            cursor: runActive ? 'not-allowed' : 'pointer',
            fontSize: 11,
            fontWeight: 600,
            color: STATUS.danger.text,
            padding: '6px 12px',
            borderRadius: 7,
            opacity: runActive ? 0.5 : 1,
          }}
        >
          Delete group
        </button>
      </div>
    </div>
  );
}

export default StepsTable;
export { pairWithLatestRun, stepStatusLabel };
